// Offline attachment queue (P12, web adapter). Attaching stores the bytes and a pending file chip in ONE IndexedDB
// transaction ("saved on device"); the uploader sends idempotent PUTs (client file id + SHA-256), so a lost response
// or a reload retries without creating duplicates. Server `file` pull entities are the source of truth for a task's
// files; removals are queued the same way. Rejected uploads keep their bytes for recovery (download from device).
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { IDBPDatabase } from 'idb';
import type { FileRef } from '@/domain/model';
import type { TaskRecord } from './diff';
import type { OutboxRecord } from './ops';
import { LocalStorageFullError, isQuotaError, type FileQueueRecord, type TaskPanesDB } from '@/db/localDb';
import { TransportError } from '@/app/ports';

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
const MAX_BACKOFF = 5 * 60_000;

export interface ServerFile { id: string; task_id: string | null; /** v10.1 the file's project (project comment images have no task) */ project_id?: string; name: string; size: number; mime: string; sha256: string; created_at: string; uploaded_by: string; /** v7: 'note_image' = shown in the task's notes */ role?: string }

export interface FileTransport {
  upload(workspaceId: string, taskId: string, fileId: string, name: string, sha256: string, body: Blob, role?: FileRole): Promise<ServerFile>;
  remove(workspaceId: string, fileId: string): Promise<void>;
}

/** v10.1: `pt-{projectId}` is a project's comment thread; its images are project files (no task). */
export type FileRole = 'note_image' | 'comment_image' | 'project_comment_image';
export const PT = 'pt-';
type Holder = { files?: FileRef[] };
/** The record holding a queued file's chip: the task, or (project comment images) the project. */
async function holderGet(tx: any, holderId: string): Promise<[string, Holder | undefined]> {
  const store = holderId.startsWith(PT) ? 'projects' : 'tasks', id = holderId.startsWith(PT) ? holderId.slice(PT.length) : holderId;
  return [store, (await tx.objectStore(store).get(id)) as Holder | undefined];
}

export const fileUrl = (workspaceId: string, fileId: string) => `/api/v1/workspaces/${workspaceId}/files/${fileId}`;

/** Bytes of a queued upload as a Blob (null for removals). */
export function toBlob(r: FileQueueRecord): Blob | null {
  return r.bytes ? new Blob([r.bytes], { type: r.mime }) : null;
}

export async function sha256Hex(data: Blob | ArrayBuffer): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', data instanceof ArrayBuffer ? data : await data.arrayBuffer());
  return [...new Uint8Array(d)].map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Server file → chip on its task (pending flag cleared). */
export function applyServerFile(task: Holder, f: ServerFile, workspaceId: string): void {
  const prev = (task.files || []).find(x => x.id === f.id);
  const ref: FileRef = { id: f.id, name: f.name, size: f.size, url: fileUrl(workspaceId, f.id), ...(f.role === 'note_image' || f.role === 'comment_image' || f.role === 'project_comment_image' ? { role: f.role } : {}), ...(prev?.ar ? { ar: prev.ar } : {}) };
  const files = (task.files || []).filter(x => x.id !== f.id);
  task.files = [...files, ref];
}

export class FileQueue {
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private readonly o: { db: IDBPDatabase<TaskPanesDB>; workspaceId: string; transport: FileTransport; newId(): string; now?: () => number; onChanged(): void }) {}
  private now() { return (this.o.now || Date.now)(); }

  /** Atomically stores the bytes and the pending chip. Throws LocalStorageFullError when the device is full. */
  async enqueue(taskId: string, file: File, opts: { role?: FileRole; ar?: number } = {}): Promise<FileRef> {
    if (file.size > MAX_UPLOAD_BYTES) throw new TransportError('rejected', 'file_too_large', 413, 'file_too_large');
    if (file.size === 0) throw new TransportError('rejected', 'empty_file', 422, 'empty_file');
    const bytes = await file.arrayBuffer();
    const sha = await sha256Hex(bytes);
    const id = this.o.newId();
    const rec: FileQueueRecord = { id, kind: 'upload', taskId, name: file.name.slice(0, 255) || 'file', size: file.size, sha256: sha, bytes, mime: file.type || 'application/octet-stream', state: 'queued', attempts: 0, next_attempt_at: 0, created_at: this.now(), ...(opts.role ? { role: opts.role } : {}) };
    const ref: FileRef = { id, name: rec.name, size: file.size, pending: true, ...(opts.role ? { role: opts.role } : {}), ...(opts.ar ? { ar: opts.ar } : {}) };
    try {
      const tx = this.o.db.transaction(['fileQueue', 'tasks', 'projects'], 'readwrite');
      const [store, task] = await holderGet(tx, taskId);
      await tx.objectStore('fileQueue').put(rec);
      if (task) { task.files = [...(task.files || []), ref]; await tx.objectStore(store as 'tasks').put(task as TaskRecord); }
      await tx.done;
    } catch (e) { if (isQuotaError(e)) throw new LocalStorageFullError(e); throw e; }
    this.o.onChanged();
    return ref;
  }

  /** Removes a chip now; a never-uploaded file is simply dropped, an uploaded one gets a queued server removal. */
  async remove(taskId: string, fileId: string): Promise<void> {
    const tx = this.o.db.transaction(['fileQueue', 'tasks', 'projects'], 'readwrite');
    const q = tx.objectStore('fileQueue');
    const queued = (await q.get(fileId)) as FileQueueRecord | undefined;
    if (queued && queued.kind === 'upload' && queued.state !== 'uploading') await q.delete(fileId);
    else await q.put({ id: fileId, kind: 'delete', taskId, name: '', size: 0, sha256: '', bytes: null, mime: '', state: 'queued', attempts: 0, next_attempt_at: 0, created_at: this.now() });
    const [store, task] = await holderGet(tx, taskId);
    if (task) { task.files = (task.files || []).filter(f => f.id !== fileId); await tx.objectStore(store as 'tasks').put(task as TaskRecord); }
    await tx.done;
    this.o.onChanged();
  }

  /** Ids with a queued removal (pull must not resurrect them meanwhile). */
  async pendingRemovals(): Promise<Set<string>> {
    return new Set(((await this.o.db.getAll('fileQueue')) as FileQueueRecord[]).filter(r => r.kind === 'delete').map(r => r.id));
  }

  async pendingCount(): Promise<number> {
    return ((await this.o.db.getAll('fileQueue')) as FileQueueRecord[]).filter(r => r.state !== 'rejected').length;
  }

  /** Local bytes of a not-yet-uploaded (or rejected) file, for opening/recovery on this device. */
  async localBlob(fileId: string): Promise<Blob | null> {
    const r = (await this.o.db.get('fileQueue', fileId)) as FileQueueRecord | undefined;
    return r ? toBlob(r) : null;
  }

  /**
   * Sends due queue entries one by one. An upload waits while its task's create is still unsent (the server would
   * not know the task). Network/5xx → backoff with jitter; auth → stop (the engine pauses sync); 4xx → rejected,
   * bytes kept. Returns true when something changed locally.
   */
  async process(outbox: OutboxRecord[]): Promise<boolean> {
    const unsentTasks = new Set(outbox.filter(r => r.op.command_type === 'createTask' || r.op.command_type === 'createProject').map(r => (r.op.command_type === 'createProject' ? PT : '') + r.op.entity_id));
    let changed = false;
    const all = ((await this.o.db.getAll('fileQueue')) as FileQueueRecord[]).sort((a, b) => a.created_at - b.created_at);
    for (const r of all) {
      if (r.state === 'rejected' || r.next_attempt_at > this.now() || (r.kind === 'upload' && unsentTasks.has(r.taskId))) continue;
      try {
        if (r.kind === 'upload') {
          await this.o.db.put('fileQueue', { ...r, state: 'uploading' });
          const f = await this.o.transport.upload(this.o.workspaceId, r.taskId, r.id, r.name, r.sha256, toBlob(r)!, r.role);
          const tx = this.o.db.transaction(['fileQueue', 'tasks', 'projects'], 'readwrite');
          await tx.objectStore('fileQueue').delete(r.id);
          const [store, task] = await holderGet(tx, r.taskId);
          if (task && (task.files || []).some(x => x.id === r.id)) { applyServerFile(task, f, this.o.workspaceId); await tx.objectStore(store as 'tasks').put(task as TaskRecord); }
          await tx.done;
        } else {
          await this.o.transport.remove(this.o.workspaceId, r.id);
          await this.o.db.delete('fileQueue', r.id);
        }
        changed = true;
      } catch (e) {
        const te = e instanceof TransportError ? e : null;
        if (te?.kind === 'auth') { await this.o.db.put('fileQueue', { ...r, state: 'queued' }); throw e; }
        if (r.kind === 'delete' && te?.status === 404) { await this.o.db.delete('fileQueue', r.id); changed = true; continue; }
        const retryable = !te || te.kind === 'network' || te.kind === 'server' || te.code === 'upload_in_progress';
        if (retryable) {
          const n = r.attempts + 1, wait = Math.round(Math.min(MAX_BACKOFF, 1000 * 2 ** (n - 1)) * (0.5 + Math.random() / 2));
          await this.o.db.put('fileQueue', { ...r, state: 'queued', attempts: n, next_attempt_at: this.now() + wait, last_error: (e as Error).message });
          if (te?.kind === 'network') break; // offline: stop for now, the engine retries on reconnect
        } else {
          // Rejected (too large, type, quota, no access…): keep the bytes, mark the chip, never retry silently.
          const tx = this.o.db.transaction(['fileQueue', 'tasks', 'projects'], 'readwrite');
          await tx.objectStore('fileQueue').put({ ...r, state: 'rejected', last_error: te?.code || 'rejected' });
          const [store, task] = await holderGet(tx, r.taskId);
          if (task) { task.files = (task.files || []).map(x => (x.id === r.id ? { ...x, pending: false, error: te?.code || 'rejected' } : x)); await tx.objectStore(store as 'tasks').put(task as TaskRecord); }
          await tx.done;
          changed = true;
        }
      }
    }
    if (changed) this.o.onChanged();
    return changed;
  }

  /** Earliest due retry, for the engine's wake-up timer. */
  async nextDue(): Promise<number | null> {
    const due = ((await this.o.db.getAll('fileQueue')) as FileQueueRecord[]).filter(r => r.state === 'queued').map(r => r.next_attempt_at);
    return due.length ? Math.min(...due) : null;
  }

  dispose(): void { if (this.timer) clearTimeout(this.timer); }
}
